<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { uniqueId } from 'lodash-es';
import type {
  DirectoryField,
  DirectoryFormPayload,
} from '@/modules/counterparty/counterparty-master/composables/use-manager-directory-fields.ts';
import { COMMENT_MAX_LENGTH } from '@/modules/common/consts/max-length.ts';
import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import { ValueUtil } from '@/modules/common/utils/value.util.ts';
import Validator from '@/modules/common/utils/validator.ts';
import { queryClient } from '@/tanstack-query.ts';
import { TimezoneQueryOptions } from '@/modules/counterparty/query-options/timezone.query-options.ts';
import { useFormTooltips } from '@/modules/ui/composables/use-form-tooltips.ts';
import { findDefaultTimezone } from '@/modules/counterparty/counterparty-master/utils/find-default-timezone.util.ts';
import CancelButton from '@/modules/ui/components/buttons/CancelButton.vue';
import DeleteButton from '@/modules/ui/components/buttons/DeleteButton.vue';
import SaveButton from '@/modules/ui/components/buttons/SaveButton.vue';
import ModalForm from '@/modules/ui/components/common/ModalForm.vue';
import UiCombobox from '@/modules/ui/components/ui/UiCombobox.vue';
import UiField from '@/modules/ui/components/ui/UiField.vue';
import UiInput from '@/modules/ui/components/ui/UiInput.vue';
import UiTextarea from '@/modules/ui/components/ui/UiTextarea.vue';

const isOpen = defineModel<boolean>({ required: true });

const props = defineProps<{
  field: DirectoryField;
  mode: 'add' | 'edit';
  initialName?: string;
  initialNote?: string;
  initialCenter?: string;
  initialTzId?: number | null;
  loading?: boolean;
  canDelete?: boolean;
  isNameTaken: (name: string) => boolean;
}>();

const emit = defineEmits<{
  submit: [payload: DirectoryFormPayload];
  delete: [];
}>();

const name = ref('');
const note = ref('');
const center = ref('');
const timezone = ref<Option<number> | null>(null);
const showErrors = ref(false);
const { tooltipsDisabled, onInput } = useFormTooltips();
const fieldIds = {
  name: uniqueId(IdPrefix.Control),
  center: uniqueId(IdPrefix.Control),
  timezone: uniqueId(IdPrefix.Control),
  note: uniqueId(IdPrefix.Control),
};
let keepOpenWhileLoading = false;

const isEdit = computed(() => props.mode === 'edit');
const isRegion = computed(() => props.field === 'region');

const title = computed(() =>
  isEdit.value
    ? `counterparty.master.steps.manager.directory.${props.field}.editTitle`
    : `counterparty.master.steps.manager.directory.${props.field}.addTitle`
);

const namePlaceholder = computed(() => `counterparty.master.steps.manager.directory.${props.field}.name`);
const nameTooltip = computed(() => ValueUtil.placeholderTooltip(namePlaceholder.value, name.value));
const centerTooltip = computed(() =>
  ValueUtil.placeholderTooltip('counterparty.master.steps.manager.directory.region.city', center.value)
);
const noteTooltip = computed(() => ValueUtil.placeholderTooltip('ui.common.placeholder.enterNote', note.value));
const timezoneTooltip = computed(() =>
  ValueUtil.placeholderTooltip('counterparty.master.steps.manager.directory.region.timezone', timezone.value)
);

const nameError = computed(() => {
  const required = Validator.required(name.value.trim());

  if (required) {
    return required;
  }

  if (props.isNameTaken(name.value.trim())) {
    return `counterparty.notification.error.${props.field}.duplicate`;
  }

  return undefined;
});

const timezoneError = computed(() => (isRegion.value ? Validator.required(timezone.value) : undefined));

async function getTimezoneOptions(signal: AbortSignal): Promise<Option<number>[]> {
  const zones = await queryClient.ensureQueryData(TimezoneQueryOptions.getList(signal));
  return zones.map((zone) => ({ code: zone.id, label: zone.name }));
}

async function resolveInitialTimezone(tzId: number | null | undefined) {
  const zones = await queryClient.ensureQueryData(TimezoneQueryOptions.getList());

  if (tzId != null) {
    const current = zones.find((zone) => zone.id === tzId);

    if (current) {
      return { code: current.id, label: current.name };
    }

    return { code: tzId, label: String(tzId) };
  }

  const fallback = findDefaultTimezone(zones);
  return fallback ? { code: fallback.id, label: fallback.name } : null;
}

watch(isOpen, async (open) => {
  if (!open) {
    if (props.loading) {
      keepOpenWhileLoading = true;
      isOpen.value = true;
    }
    return;
  }

  if (keepOpenWhileLoading) {
    keepOpenWhileLoading = false;
    return;
  }

  name.value = props.initialName ?? '';
  note.value = props.initialNote ?? '';
  center.value = props.initialCenter ?? '';
  timezone.value = null;
  showErrors.value = false;

  if (!isRegion.value) {
    return;
  }

  const nextTimezone = await resolveInitialTimezone(props.initialTzId);

  if (!isOpen.value) {
    return;
  }

  timezone.value = nextTimezone;
});

function submit() {
  if (props.loading) {
    return;
  }

  showErrors.value = true;

  if (nameError.value || timezoneError.value) {
    return;
  }

  emit('submit', {
    name: name.value.trim(),
    note: note.value.trim(),
    ...(isRegion.value
      ? {
          center: center.value.trim(),
          tzId: timezone.value?.code,
        }
      : {}),
  });
}
</script>

<template>
  <ModalForm
    v-model:open="isOpen"
    width="xs"
    :confirm-on-dirty="false"
    :title
  >
    <template #body>
      <div class="directory-form">
        <UiField
          :id="fieldIds.name"
          stretch
          with-tooltip
          :tooltip="nameTooltip"
          :tooltip-disabled="tooltipsDisabled"
          :error="showErrors ? nameError : undefined"
        >
          <UiInput
            :id="fieldIds.name"
            v-model="name"
            no-clear
            :placeholder="namePlaceholder"
            :max-length="255"
            :error="showErrors && !!nameError"
            :disabled="loading"
            @keydown.enter.prevent="submit"
            @update:model-value="onInput"
          />
        </UiField>

        <template v-if="isRegion">
          <UiField
            :id="fieldIds.center"
            stretch
            with-tooltip
            :tooltip="centerTooltip"
            :tooltip-disabled="tooltipsDisabled"
          >
            <UiInput
              :id="fieldIds.center"
              v-model="center"
              no-clear
              placeholder="counterparty.master.steps.manager.directory.region.city"
              :max-length="255"
              :disabled="loading"
              @update:model-value="onInput"
            />
          </UiField>

          <UiField
            :id="fieldIds.timezone"
            stretch
            with-tooltip
            :tooltip="timezoneTooltip"
          >
            <UiCombobox
              v-model="timezone"
              placeholder="counterparty.master.steps.manager.directory.region.timezone"
              :options="getTimezoneOptions"
              :error="showErrors && !!timezoneError"
              :no-clear="true"
              :disabled="loading"
              update-after-load
              with-search
              inline-search
              :search-max-length="255"
            />
          </UiField>
        </template>

        <UiField
          :id="fieldIds.note"
          stretch
          with-tooltip
          :tooltip="noteTooltip"
          :tooltip-disabled="tooltipsDisabled"
        >
          <UiTextarea
            :id="fieldIds.note"
            v-model="note"
            placeholder="ui.common.placeholder.enterNote"
            :max-length="COMMENT_MAX_LENGTH"
            :rows="3"
            :disabled="loading"
            @update:model-value="onInput"
          />
        </UiField>
      </div>
    </template>

    <template #footer>
      <DeleteButton
        v-if="isEdit && canDelete"
        class="delete"
        :variant="2"
        :loading
        @click="emit('delete')"
      />

      <CancelButton
        label="counterparty.master.steps.manager.directory.cancel"
        :disabled="loading"
        @click="isOpen = false"
      />

      <SaveButton
        no-icon
        :loading
        @click="submit"
      />
    </template>
  </ModalForm>
</template>

<style scoped lang="scss">
.directory-form {
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  gap: 12px;
}

.delete {
  margin-right: auto;
}
</style>
